import { useEffect, useState } from "react";
import { PlusCircle, Loader2, AlertTriangle, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { api } from "../../lib/api";

export default function PrescriptionRecheck({ isPro, onUpgrade }) {
  const [open, setOpen] = useState(false);
  const [meds, setMeds] = useState([]);
  const [medId, setMedId] = useState("");
  const [dose, setDose] = useState("");
  const [frequency, setFrequency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open || meds.length) return;
    api.get("/medications").then(({ data }) => setMeds(data || [])).catch(() => {});
  }, [open, meds.length]);

  async function submit() {
    if (!medId) { toast.error("Pick the new medication first."); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/recheck", {
        medication_id: medId,
        dose: dose || null,
        frequency: frequency || null,
        start_date: startDate || null,
      });
      setResult(data);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Re-checking routines is a Pro feature.");
        onUpgrade?.();
      } else {
        toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Re-check failed");
      }
    } finally { setBusy(false); }
  }

  return (
    <div data-testid="prescription-recheck" className="mb-10 rounded-2xl border border-surface-line bg-white p-6 shadow-subtle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-ink tracking-tight">Prescribed something new?</h2>
          <p className="mt-1 text-[14px] text-ink-soft max-w-md">
            We'll re-check the selected profile’s saved routines against the new medication and email an updated Discussion Report to the account owner.
          </p>
        </div>
        {!open && (
          <Button data-testid="recheck-open" onClick={() => setOpen(true)} className="w-full sm:w-auto h-auto min-h-10 py-2.5 justify-center whitespace-normal text-center leading-tight rounded-xl bg-ink hover:bg-ink-soft text-cream-50">
            <PlusCircle className="w-4 h-4 mr-2 shrink-0" /> I was prescribed a new medication
          </Button>
        )}
      </div>

      {open && (
        <div data-testid="recheck-form" className="mt-5 border-t border-surface-line pt-5 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide font-bold text-ink-mute">New medication</label>
            <Select value={medId} onValueChange={setMedId}>
              <SelectTrigger data-testid="recheck-med-select" className="mt-1 h-10">
                <SelectValue placeholder="Choose a medication…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {meds.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name || m.generic_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input value={dose} onChange={(e) => setDose(e.target.value)} placeholder="Dose (optional)"
              data-testid="recheck-dose" className="h-10 px-3 rounded-lg border border-surface-line bg-white text-sm text-ink" />
            <input value={frequency} onChange={(e) => setFrequency(e.target.value)} placeholder="Frequency (optional)"
              data-testid="recheck-frequency" className="h-10 px-3 rounded-lg border border-surface-line bg-white text-sm text-ink" />
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              data-testid="recheck-start-date" className="h-10 px-3 rounded-lg border border-surface-line bg-white text-sm text-ink" />
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="recheck-submit" onClick={submit} disabled={busy} className="rounded-xl bg-ink hover:bg-ink-soft text-cream-50">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-checking…</> : "Re-check my routines"}
            </Button>
            <button className="text-[13px] text-ink-mute hover:text-ink" onClick={() => { setOpen(false); setResult(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {result && (
        <div data-testid="recheck-result" className="mt-5 rounded-xl border border-surface-line bg-cream-100/50 p-5">
          <div className="flex items-center gap-2">
            {result.products_to_change > 0
              ? <AlertTriangle className="w-5 h-5 text-caution-deep" />
              : <CheckCircle2 className="w-5 h-5 text-safe-deep" />}
            <span data-testid="recheck-summary" className="text-[16px] font-bold text-ink">
              {result.products_to_change > 0
                ? `${result.products_to_change} product${result.products_to_change !== 1 ? "s" : ""} ${result.products_to_change !== 1 ? "need" : "needs"} to change`
                : "Nothing in your routines needs to change"}
            </span>
          </div>
          <p className="mt-1 text-[13px] text-ink-soft">
            We re-checked {result.routines_checked} saved routine{result.routines_checked !== 1 ? "s" : ""} against {result.medication_name}.
          </p>
          {result.details?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {result.details.map((d, i) => (
                <li key={i} className="text-[13px] text-ink-soft flex items-start gap-2">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-caution-deep shrink-0" />
                  <span><strong className="text-ink">{d.product_name}</strong> in “{d.routine_label}” → {d.after === "AVOID" ? "Avoid" : "Use with caution"}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-4 flex items-center gap-2 text-[13px] text-ink-soft">
            <Mail className="w-4 h-4" />
            {result.emailed
              ? "Your updated Discussion Report has been emailed to you."
              : "Your updated Discussion Report is ready — email delivery is active in production."}
          </div>
        </div>
      )}
    </div>
  );
}
